import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { buyersApi } from "@/api/buyers";
import { Skeleton, StatusBadge, Timestamp } from "@/design-system/components/ui";
import { qualifiedAmountLabel } from "./prequalification/qualifiedAmount";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { DetailField, DetailFieldList, DetailSection } from "./detailBits";
import { InlineQueryError } from "./queryStates";

/**
 * The buyer's linked prequalification, as a READ-ONLY summary on Overview.
 *
 * <p>It sits on Overview, in the rail, beside the "Prequalification completed"
 * milestone row that reports the same event — the answer at a glance, with the
 * way through to where the acting happens.</p>
 *
 * <p>🔴 <b>Every ACTION lives on the Prequalification tab.</b> Link, change,
 * unlink and the candidate picker are there, alongside the invitations and
 * attempts lists that give them context. One place to LOOK, one place to ACT —
 * a specialist reading Overview should see the answer, and a specialist
 * changing it should see what else the buyer has completed while they decide.
 * Keeping a second set of buttons here would mean two surfaces that must agree
 * about what "linked" means, and Layout Standard §4.1a forbids the door on an
 * Overview anyway.</p>
 *
 * <p>⚠️ <b>It briefly lived INSIDE the tab it links to</b>, when Qualification
 * and Prequalification were merged into one. "Manage prequalification →" then
 * pointed at the surface the panel was already rendered on, one screen above
 * the actions it was offering to take you to, and the facts were rendered
 * twice. The split put it back; the doc comments that had gone stale about it
 * are these ones.</p>
 *
 * <p><b>The props are unchanged</b> (`buyerLeadId`, `canEdit`) even though
 * `canEdit` no longer gates a button: it gates the invitation to go and act.
 * Showing a read-only viewer a link to a tab whose actions they cannot use is a
 * dead end presented as an affordance.</p>
 *
 * <p>Read-only against prequalification, as ever — this panel writes
 * nothing.</p>
 */
export function PrequalificationLinkPanel({
  buyerLeadId,
  canEdit,
}: {
  buyerLeadId: string;
  canEdit: boolean;
}) {
  const link = useQuery({
    queryKey: buyerKeys.prequalification(buyerLeadId),
    queryFn: () => buyersApi.getPrequalification(buyerLeadId),
    enabled: Boolean(buyerLeadId),
  });

  if (link.isLoading) {
    return (
      <DetailSection title="Prequalification">
        <Skeleton className="h-24 w-full" />
      </DetailSection>
    );
  }

  // "No prequalification is linked" is a claim about the buyer's record, and a
  // failed read is no evidence for it — the worse direction of the two, because
  // it sends a specialist off to link one that is already there.
  if (link.isError) {
    return (
      <DetailSection title="Prequalification">
        <InlineQueryError
          what="the linked prequalification"
          onRetry={() => void link.refetch()}
          retrying={link.isFetching}
        />
      </DetailSection>
    );
  }

  const l = link.data;
  const linked = Boolean(l?.submissionId);
  const tabHref = `/buyers/${buyerLeadId}?tab=prequalification`;

  return (
    <DetailSection title="Prequalification">
      {linked ? (
        <>
          <div className="flex flex-wrap items-center gap-space-3 mb-space-3">
            <StatusBadge tone={l!.hasLetter ? "ok" : "warn"}>
              {l!.hasLetter ? "Letter on file" : "No letter yet"}
            </StatusBadge>
            {!l!.hasLetter && (
              <span className="text-caption text-gray-mid">
                A submission package embeds the letter, so it cannot be generated
                until one exists.
              </span>
            )}
          </div>

          <DetailFieldList>
            <DetailField label="Reference" value={l!.referenceNumber} />
            <DetailField label="Outcome" value={l!.outcome} />
            <DetailField label="Qualified buying power" value={qualifiedAmountLabel(l!)} />
            <DetailField
              label="Submitted"
              value={l!.submittedAtUtc ? <Timestamp value={l!.submittedAtUtc} /> : null}
            />
            <DetailField
              label="Linked"
              value={l!.linkedAtUtc ? <Timestamp value={l!.linkedAtUtc} /> : null}
            />
          </DetailFieldList>
        </>
      ) : (
        <p className="text-small text-gray-mid m-0 mb-space-3">
          No prequalification is linked. A submission package embeds the buyer's
          Phoenix prequalification letter, so one is needed before a package can
          be generated.
        </p>
      )}

      {canEdit && (
        <Link
          to={tabHref}
          data-testid="prequal-open-tab"
          className="text-small text-gold-shadow hover:underline"
        >
          {linked
            ? "Manage prequalification →"
            : "Invite or link a prequalification →"}
        </Link>
      )}
    </DetailSection>
  );
}
